import { iconMarkup } from '../icons.js';
import { mountTooltips, setTooltip } from '../tooltips.js';
import { mountUiScale } from '../shell/ui-scale.js';

/**
 * The in-world HUD's page markup (the party column, menu and command rail are drawn in 3D over the stage), in two fragments so a page keeps the shell's stacking order: the status overlays go near the top of
 * the page, the pack dialog, command dock and area banner near the end. The game's shell and the scenario viewer both use these.
 */
export function hudOverlayMarkup() {
  return `
    <div id="paused-banner" hidden>${iconMarkup('pause')} Paused</div>
    <div id="combat-status" class="combat-status" hidden></div>
    <aside id="combat-log" class="combat-log" aria-label="Combat log" hidden></aside>`;
}

export function hudDockMarkup() {
  return `
    <dialog id="supplies-panel" class="inventory-modal" aria-labelledby="supplies-title" aria-describedby="supplies-subtitle" hidden>
      <header class="inventory-header"><div><p class="inventory-eyebrow">Character & equipment</p><h2 id="supplies-title">Inventory</h2><p id="supplies-subtitle"></p></div><button id="supplies-close" class="quiet inventory-close" autofocus aria-label="Close character inventory">${iconMarkup('close')}</button></header>
      <div id="supplies-body" class="inventory-layout"></div>
    </dialog>
    <dialog id="reading-panel" class="reading-backdrop" aria-labelledby="reading-title">
      <article id="reading-page" class="reading-page" tabindex="-1" aria-labelledby="reading-title">
        <div class="reading-sheet">
          <button id="reading-close" class="quiet glyph-button" aria-label="Close">${iconMarkup('close')}</button>
          <h2 id="reading-title"></h2>
          <div id="reading-body"></div>
          <p id="reading-signature" hidden></p>
        </div>
      </article>
    </dialog>
    <footer id="hud" aria-label="Party panels" hidden>
      <div class="hud-panels">
      <section id="behavior-panel" class="behavior-panel" aria-labelledby="behavior-title" hidden>
        <div class="behavior-header"><h2 id="behavior-title">Hero behavior</h2><button id="behavior-close" class="quiet glyph-button">${iconMarkup('close')}</button></div>
        <div class="behavior-body">
          <div class="behavior-field"><span class="behavior-label">Autonomy</span><div id="behavior-autonomy" class="behavior-choices" role="group" aria-label="Autonomy"></div></div>
          <div class="behavior-field"><span class="behavior-label">Stance</span><div id="behavior-stance" class="behavior-choices" role="group" aria-label="Stance"></div></div>
          <div class="behavior-switches">
            <label id="behavior-defend-row" class="switch"><input id="behavior-defend" type="checkbox"> Defend</label>
            <label id="behavior-opportunity-row" class="switch"><input id="behavior-opportunity" type="checkbox"> Opportunity attacks</label>
            <label id="behavior-interpose-row" class="switch"><input id="behavior-interpose" type="checkbox"> Interpose</label>
            <label id="behavior-healing-row" class="switch"><input id="behavior-healing" type="checkbox"> Use potions</label>
            <label id="behavior-inspiration-row" class="switch"><input id="behavior-inspiration" type="checkbox"> Use Inspiration</label>
            <label id="behavior-relentless-row" class="switch"><input id="behavior-relentless" type="checkbox"><span>Use Relentless <span id="behavior-relentless-state" class="behavior-spent" hidden></span></span></label>
          </div>
          <div class="behavior-field"><span class="behavior-label">Targeting</span><div id="behavior-targeting" class="behavior-choices" role="group" aria-label="Targeting"></div></div>
          <div id="behavior-quarry-row" class="behavior-field"><span class="behavior-label">Hunter’s Mark</span><div id="behavior-quarry" class="behavior-choices" role="group" aria-label="Hunter’s Mark bonus"></div></div>
          <div id="behavior-fury-row" class="behavior-field"><span class="behavior-label">That all you got?!</span><div id="behavior-fury" class="behavior-choices" role="group" aria-label="That all you got?!"></div></div>
          <label id="behavior-ally-row" class="behavior-field"><span class="behavior-label">Protect</span><select id="behavior-ally" aria-label="Protected ally"></select></label>
          <button id="resume-ai" class="secondary" hidden>Resume AI</button>
        </div>
      </section>
      <section id="loot-panel" class="behavior-panel supplies-panel loot-panel" aria-labelledby="loot-title" hidden>
        <div class="behavior-header"><h2 id="loot-title">Loot</h2><button id="loot-close" class="quiet glyph-button">${iconMarkup('close')}</button></div>
        <div id="loot-body" class="behavior-body"></div>
      </section>
      <section id="rest-panel" class="behavior-panel supplies-panel rest-panel" aria-labelledby="rest-title" hidden>
        <div class="behavior-header"><h2 id="rest-title">Rest</h2><button id="rest-close" class="quiet glyph-button">${iconMarkup('close')}</button></div>
        <div id="rest-body" class="behavior-body"></div>
      </section>
      </div>
    </footer>
    <div id="area-banner" class="area-banner" hidden></div>`;
}

/** Tooltips for the HUD's controls; call once its markup is in the page. */
export function mountHudHints() {
  const hints: Record<string, [string, string]> = {
    'behavior-close': ['Close hero behavior · B', 'Close behavior panel'],
    'supplies-close': ['Close pack · I', 'Close pack panel'],
    'loot-close': ['Close window', 'Close loot window'],
    'reading-close': ['Close · Esc', 'Close the page'],
    'rest-close': ['Close rest panel', 'Close rest panel'],
  };
  for (const [id, [hint, label]] of Object.entries(hints)) setTooltip(document.getElementById(id)!, hint, label);
}

/** Puts the HUD into a page that has no game shell (the scenario viewer): overlays after `stage`, the dock at the end of `root`. */
export function mountWorldHudMarkup(root: HTMLElement, stage: HTMLElement) {
  stage.insertAdjacentHTML('afterend', hudOverlayMarkup());
  root.insertAdjacentHTML('beforeend', `${hudDockMarkup()}<div id="toast" hidden></div>`);
  mountHudHints(); mountTooltips(root); mountUiScale();
}
